import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { FileJson } from 'lucide-react'
import { formatSecondsToDHM } from './utils/formatTime'
import { formatBytes } from './utils/formatSize'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { ObjectStatusPair } from '@/components/ui/object-status-pair'

function StatusIndicator({ status }: { status: number | null }) {
    if (status === null || status === undefined) {
        return <span className="text-muted-foreground">-</span>
    }
    return <AnalyticsStatusIndicator status={status} size="h-4 w-4" />
}

export interface ServiceTableRow {
    sourceID: string
    description: string
    sourceLayer: number | null
    iconFilename: string | null
    calculatedRPO: number | null
    endToEndTime: number | null
    metadata: string | null
    vaultFiles: string | null
    objectStatus: number | null
    objectAnalytics: number | null
    redHere?: number
    redBelow?: number
    indicatorsBelow?: number
    redBelowIds?: string[]
    indicatorsBelowIds?: string[]
    objectSize: number | null
}

export function createServiceTableColumns(
    t: (key: string) => string,
    onShowJson?: (title: string, content: string | null) => void
): ColumnDef<ServiceTableRow>[] {
    return [
        {
            accessorKey: 'sourceID',
            header: t('objects.service.objectID'),
            size: 300,
            minSize: 200,
            maxSize: 450,
            cell: ({ row }) => {
                const sourceID = row.getValue('sourceID') as string
                const description = row.original.description
                const sourceLayer = row.original.sourceLayer
                const hoverText = [sourceID, description].filter(Boolean).join(' — ')
                // Indentation: higher layer = less indent (matches original backend logic)
                const indent = Math.max(0, 12 - (sourceLayer ?? 0) * 2)
                return (
                    <div
                        className="flex flex-col min-w-0"
                        style={{ paddingLeft: `${indent * 0.5}rem` }}
                        title={hoverText || undefined}
                    >
                        <span className="text-sm font-medium truncate">{sourceID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate">{description}</span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: 40,
            minSize: 40,
            maxSize: 40,
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <EntityIcon
                            src={buildIconPath(iconFilename)}
                            alt=""
                            className="h-8 w-8"
                        />
                    </div>
                )
            },
        },
        {
            accessorKey: 'calculatedRPO',
            header: () => <div className="text-right w-full">{t('objects.service.calculatedRPO')}</div>,
            size: 120,
            minSize: 100,
            maxSize: 150,
            cell: ({ row }) => {
                const rpo = row.getValue('calculatedRPO') as number | null
                return (
                    <span className="text-right w-full block text-sm">
                        {rpo !== null && rpo !== undefined ? formatSecondsToDHM(rpo) : '-'}
                    </span>
                )
            },
        },
        {
            accessorKey: 'endToEndTime',
            header: () => <div className="text-right w-full">{t('objects.service.endToEndRTO')}</div>,
            size: 120,
            minSize: 100,
            maxSize: 150,
            cell: ({ row }) => {
                const time = row.getValue('endToEndTime') as number | null
                return (
                    <span className="text-right w-full block text-sm">
                        {time !== null && time !== undefined ? formatSecondsToDHM(time) : '-'}
                    </span>
                )
            },
        },
        {
            accessorKey: 'metadata',
            header: t('objects.service.metadata'),
            size: 80,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => {
                const metadata = row.getValue('metadata') as string | null
                if (!metadata) return null
                return (
                    <div className="flex items-center justify-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => onShowJson?.(t('objects.service.metadata'), metadata)}
                        >
                            <FileJson className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'vaultFiles',
            header: t('objects.service.vaultFiles'),
            size: 80,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => {
                const vaultFiles = row.getValue('vaultFiles') as string | null
                if (!vaultFiles) return null
                return (
                    <div className="flex items-center justify-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => onShowJson?.(t('objects.service.vaultFiles'), vaultFiles)}
                        >
                            <FileJson className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    </div>
                )
            },
        },
        // Scan = the object's own latest scan; Hierarchy = rolled up, with the counts beneath. See
        // ObjectStatusPair — the same two marks as the objects list and the object page.
        {
            accessorKey: 'objectAnalytics',
            header: () => <div className="text-center w-full">{t('objects.statusPair.scan')}</div>,
            size: 70,
            minSize: 60,
            maxSize: 100,
            cell: ({ row }) => {
                const o = row.original
                if (o.objectAnalytics == null && o.objectStatus == null) return <StatusIndicator status={null} />
                return (
                    <div className="flex items-center justify-center">
                        <ObjectStatusPair variant="scan" size="h-4 w-4" scan={o.objectAnalytics} hierarchy={o.objectStatus}
                            redHere={o.redHere} redBelow={o.redBelow} indicatorsBelow={o.indicatorsBelow} redBelowIds={o.redBelowIds} indicatorsBelowIds={o.indicatorsBelowIds} />
                    </div>
                )
            },
        },
        {
            accessorKey: 'objectStatus',
            header: () => <div className="text-center w-full">{t('objects.statusPair.hierarchy')}</div>,
            size: 100,
            minSize: 80,
            maxSize: 130,
            cell: ({ row }) => {
                const o = row.original
                if (o.objectStatus == null) return <StatusIndicator status={null} />
                return (
                    <div className="flex items-center justify-center">
                        <ObjectStatusPair variant="hierarchy" size="h-4 w-4" scan={o.objectAnalytics} hierarchy={o.objectStatus}
                            redHere={o.redHere} redBelow={o.redBelow} indicatorsBelow={o.indicatorsBelow} redBelowIds={o.redBelowIds} indicatorsBelowIds={o.indicatorsBelowIds} />
                    </div>
                )
            },
        },
        {
            accessorKey: 'objectSize',
            header: () => <div className="text-right w-full">{t('objects.service.objectSize')}</div>,
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const size = row.getValue('objectSize') as number | null
                return (
                    <span className="text-right w-full block text-sm">
                        {size !== null && size !== undefined ? formatBytes(size) : '-'}
                    </span>
                )
            },
        },
    ]
}
